import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { EASE, scrollToId, useTheme } from "./shared";
import { SoundToggle } from "./SoundToggle";

const LINKS = [
    { label: "WORK", id: "work" },
    { label: "EXPERIENCES", id: "experiences" },
    { label: "FOR ARCHITECTS", id: "architects" },
    { label: "ABOUT", id: "about" },
    { label: "CONTACT", id: "contact" },
];

export const SceneToggle = () => {
    const { theme, toggleTheme } = useTheme();
    return (
        <div data-testid="lighting-scene-toggle" className="flex border border-white/25" role="group" aria-label="Lighting scene">
            {[
                { mode: "day", label: "DAYLIGHT", testId: "scene-daylight" },
                { mode: "night", label: "EXHIBITION NIGHT", testId: "scene-night" },
            ].map((m) => (
                <button
                    key={m.mode}
                    type="button"
                    data-testid={m.testId}
                    aria-pressed={theme === m.mode}
                    onClick={() => theme !== m.mode && toggleTheme()}
                    className={`px-3 py-2 font-mono-tech text-[9px] tracking-[0.2em] transition-colors duration-700 ${
                        theme === m.mode ? "bg-white text-black" : "text-white/50 hover:text-white"
                    }`}
                >
                    {m.label}
                </button>
            ))}
        </div>
    );
};

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToId(id), open ? 350 : 0);
    };

    return (
        <>
            <header
                data-testid="main-nav"
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-700 ${
                    scrolled ? "border-b border-white/5 bg-[#070708]/70 backdrop-blur-2xl" : "bg-transparent"
                }`}
            >
                <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-12">
                    <button
                        data-testid="nav-logo"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="font-display text-sm font-bold tracking-[0.25em] text-white md:text-base"
                    >
                        MOTION TECH ARTS
                    </button>
                    <div className="flex items-center gap-4 md:gap-8">
                        <SoundToggle />
                        <div className="hidden sm:block">
                            <SceneToggle />
                        </div>
                        <nav className="hidden items-center gap-10 lg:flex">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`nav-link-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="font-mono-tech text-[10px] tracking-[0.3em] text-white/55 transition-colors duration-500 hover:text-white"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="nav-create-with-us"
                                onClick={() => go("contact")}
                                className="border border-white/30 px-6 py-3 font-mono-tech text-[10px] tracking-[0.3em] text-white transition-colors duration-500 hover:bg-white hover:text-black"
                            >
                                CREATE WITH US
                            </button>
                        </nav>
                        <button
                            data-testid="nav-menu-toggle"
                            aria-label="Menu"
                            onClick={() => setOpen(!open)}
                            className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 lg:hidden"
                        >
                            <span className={`h-px w-6 bg-white transition-transform duration-500 ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
                            <span className={`h-px w-6 bg-white transition-transform duration-500 ${open ? "-translate-y-[3.5px] -rotate-45" : ""}`} />
                        </button>
                    </div>
                </div>
            </header>
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        className="fixed inset-0 z-40 flex flex-col justify-center bg-[#050506]/95 px-8 backdrop-blur-xl lg:hidden"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        {[...LINKS, { label: "CREATE WITH US", id: "contact" }].map((l, i) => (
                            <div key={`${l.id}-${i}`} className="overflow-hidden border-b border-white/5">
                                <motion.button
                                    data-testid={`mobile-nav-link-${i}`}
                                    onClick={() => go(l.id)}
                                    className="block w-full py-5 text-left font-display text-3xl font-bold uppercase tracking-tight text-white/85"
                                    initial={{ y: "110%" }}
                                    animate={{ y: "0%" }}
                                    exit={{ y: "110%" }}
                                    transition={{ duration: 0.6, ease: EASE, delay: i * 0.06 }}
                                >
                                    {l.label}
                                </motion.button>
                            </div>
                        ))}
                        <motion.div
                            className="mt-12 sm:hidden"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5, duration: 0.6 }}
                        >
                            <p className="mb-4 font-mono-tech text-[9px] tracking-[0.35em] text-white/35">LIGHTING SCENE</p>
                            <SceneToggle />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
